<template>
  <v-chart class="chart" @finished="chartRenderFinished" :option="options" autoresize />
</template>

<script lang="ts" setup>
import type { CalculationData } from "@/types";
import { computed } from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart } from "echarts/charts";
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import { useI18n } from "vue-i18n";
import type * as echarts from "echarts";

const props = defineProps<{
  calculationData: CalculationData;
}>();

const emit = defineEmits(["animationComplete"]);

const monthlyConversion = [
  0.004241129, 0.015682588, 0.078528791, 0.147004397, 0.17107383, 0.17525652,
  0.166135257, 0.120579179, 0.082958955, 0.031921509, 0.005325592, 0.001292253,
];

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
]);

const { t } = useI18n();

const options = computed<echarts.EChartsOption>(() => {
  return {
    tooltip: {
      trigger: "item",
      formatter: (params: any) => {
        // Handles getting the correct month name for the tooltip.
        const month = t(`months.${params.dataIndex + 1}Long`);
        return `${month}: ${params.value} kWh`;
      },
      textStyle: {
        fontFamily: '"Open Sans", sans-serif',
      },
    },
    legend: {
      // Try 'horizontal'
      orient: "vertical",
      right: 20,
      top: 20,
      data: [t("chart.averageMonthly")],
      textStyle: {
        fontSize: 10,
        width: 100,
        overflow: "break",
      },
    },
    grid: {
      left: "10%",
      right: "2%",
    },
    xAxis: {
      data: [
        t("months.1Short"),
        t("months.2Short"),
        t("months.3Short"),
        t("months.4Short"),
        t("months.5Short"),
        t("months.6Short"),
        t("months.7Short"),
        t("months.8Short"),
        t("months.9Short"),
        t("months.10Short"),
        t("months.11Short"),
        t("months.12Short"),
      ],
      show: true,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 10,
        fontFamily: '"Open Sans", sans-serif',
      },
    },
    yAxis: {
      show: true,
    },
    series: [
      {
        name: "Energy Savings",
        type: "bar",
        data: monthlyConversion.map((conversion) => {
          return Math.round(props.calculationData.yearlyProd * conversion);
        }),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(0, 0, 0, 0.2)",
          },
        },
        itemStyle: {
          borderWidth: 0,
          color: "#ffaa00",
        },
      },
      {
        type: "line",
        name: t("chart.averageMonthly"),
        smooth: true,
        data: props.calculationData.currentYearlyConsumptionMonthly,
      },
    ],
  };
});

function chartRenderFinished() {
  console.log("Chart render finished!")
  emit("animationComplete");
}

</script>
